import { useAuth0 } from '@auth0/auth0-react';
import { useOrganization } from '../components/OrganizationProvider';
import { track } from './analytics';
import { getAudience, getScopes } from './auth0Utils';
import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import { InitiatePaymentResponse, PortalNotification } from '../types';
import { useQueryClient } from '@tanstack/react-query';
import { CreateAccessTokenRequest, CreateAccessTokenResponse, UpdateSmsPreferenceStatusRequest } from '../types/portalTypes';
import { SubmitAccountVerificationRequest, SubmitAccountVerificationResponse } from '../types/portalTypes/api/SubmitAccountVerification';
import { apiRoutes } from './apiRoutes';

const origin = process.env.REACT_APP_API_ORIGIN ?? ''
type ApiQueryKey = readonly [string]

export function useInitiatePayment(orderKey: string, notificationId: string, search: string, queryOptions?: Omit<UseMutationOptions<InitiatePaymentResponse, Error, undefined>, 'mutationFn'>) {
  return useApiMutation<InitiatePaymentResponse>(
    apiRoutes.initiatePayment(orderKey, notificationId, search),
    'POST',
    queryOptions
  )
}
//TODO: the PortalNotification type is wrong, the only reason this works is because we are checking if it has an ok response, not what is in it.
//  Should look at portal-api to figure out the correct type
export function usePaymentSubmit(orderKey: string, notificationId: string, search: string, queryOptions?: Omit<UseMutationOptions<PortalNotification, Error, undefined>, 'mutationFn'>) {
  return useApiMutation<PortalNotification>(apiRoutes.paymentSubmit(orderKey, notificationId, search), 'POST', queryOptions);
}

export function useSubmitAccountVerification(orderKey: string, notificationKey: string, search: string, queryOptions?: Omit<UseMutationOptions<SubmitAccountVerificationResponse, Error, SubmitAccountVerificationRequest>, 'mutationFn'>) {
  return useApiMutation<SubmitAccountVerificationResponse, SubmitAccountVerificationRequest>(
    apiRoutes.submitAccountVerification(orderKey, notificationKey, search),
    'POST',
    queryOptions
  )
}

export function useCreateAccessToken(orderKey: string, notificationId: string, search: string, queryOptions?: Omit<UseMutationOptions<CreateAccessTokenResponse, Error, CreateAccessTokenRequest>, 'mutationFn'>) {
  return useApiMutation<CreateAccessTokenResponse, CreateAccessTokenRequest>(
    apiRoutes.createAccessToken(orderKey, notificationId, search),
    'POST',
    queryOptions
  );
}

export function useDeclinePlaid(orderKey: string, notificationId: string, search: string, queryOptions?: Omit<UseMutationOptions<{ statusCode: string }, Error, undefined>, 'mutationFn'>) {
  return useApiMutation<{ statusCode: string }, undefined>(
    apiRoutes.declinePlaid(orderKey, notificationId, search),
    'POST',
    queryOptions
  )
}

export function useUserResetTask(orderKey: string, notificationId: string, search: string, queryOptions?: Omit<UseMutationOptions<any, Error, undefined>, 'mutationFn'>) {
  return useApiMutation<any>(
    apiRoutes.userResetTask(orderKey, notificationId, search),
    'POST',
    queryOptions
  );
}

export function useUserCancelTask(orderKey: string, notificationId: string, search: string, queryOptions?: Omit<UseMutationOptions<any, Error, undefined>, 'mutationFn'>) {
  return useApiMutation<any>(
    apiRoutes.userCancelTask(orderKey, notificationId, search),
    'POST',
    queryOptions
  );
}

export function useUserCompleteTask(orderKey: string, notificationId: string, search: string, queryOptions?: Omit<UseMutationOptions<any, Error, undefined>, 'mutationFn'>) {
  return useApiMutation<any>(
    apiRoutes.userCompleteTask(orderKey, notificationId, search),
    'POST',
    queryOptions
  );
}


export function useUpdateSmsPreferenceStatus(search: string, queryOptions?: Omit<UseMutationOptions<any, Error, UpdateSmsPreferenceStatusRequest>, 'mutationFn'>
) {
  return useApiMutation<
    any,
    UpdateSmsPreferenceStatusRequest
  >(
    apiRoutes.communicationPreference(search),
    'POST',
    queryOptions
  );
}

//TODO: the PortalNotification type is wrong, the only reason this works is because we are checking if it has an ok response, not what is in it.
//  Should look at portal-api to figure out the correct type
export function useFormSubmit(
  orderKey: string,
  formKey: string,
  search: string,
  queryOptions?: Omit<UseMutationOptions<PortalNotification, Error, any>, 'mutationFn'>
) {
  return useApiMutation<PortalNotification, any>(
    `order/${orderKey}/form/${formKey}/submit${search}`,
    'POST',
    queryOptions
  );
}

export function useApiMutation<TResponse, TRequestBody = undefined>(
  path: string,
  method: 'POST' | 'PUT' | 'DELETE',
  mutationOptions?: Omit<UseMutationOptions<TResponse, Error, TRequestBody>, 'mutationFn'>
) {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const organization = useOrganization();
  const scope = getScopes(organization?.MfaRequired);
  const audience = getAudience(organization?.MfaRequired);

  const mutationKey = [path] as ApiQueryKey
  const queryClient = useQueryClient()

  return useMutation<TResponse, Error, TRequestBody>({
    onSuccess: (data: any) => {
      // Cache the result so it can be reused
      queryClient.setQueryData(mutationKey, data);
    },
    ...mutationOptions,
    mutationKey,
    mutationFn: async (body: TRequestBody) => {
      const url = `${origin}/api/${path}`;

      // Try getting the token silently
      let accessToken = '';
      try {
        accessToken = await getAccessTokenSilently({ audience, scope });
      } catch (e) {
        console.warn('getAccessTokenSilently() FAILED, redirecting to login…', e);
        await loginWithRedirect({
          appState: { returnTo: window.location.pathname + window.location.search }
        });
        throw new Error('Authentication required');
      }

      const res = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: body ? JSON.stringify(body) : undefined,
      });

      if (res.ok) {
        try {
          return (await res.json()) as TResponse;
        } catch (e) {
          track('error', 'useApiMutation', {}, {
            message: 'Empty success response, returning empty object',
            error: JSON.stringify(e),
          });
          return {} as TResponse;
        }
      } else {
        let errorData: any = null;
        try {
          errorData = await res.json();
        } catch (e) {
          track('error', 'useApiMutation', {}, {
            message: 'Failed to parse error response as JSON',
            error: JSON.stringify(e),
          });
          errorData = { userErrorMessage: 'An error occurred' };
        }
        throw errorData;
      }
    },
  });
}
